import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api, { formatApiErrorDetail } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, UploadCloud, X } from "lucide-react";

export default function SettingsPage() {
  const [settings, setSettings] = useState({ company_name: "", logo_base64: "", branding_enabled: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", settings);
      toast.success("Configurações salvas");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro");
    } finally {
      setSaving(false);
    }
  };

  const onLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.error("Logo deve ter menos de 500KB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setSettings({ ...settings, logo_base64: reader.result });
    reader.readAsDataURL(file);
  };

  if (loading) {
    return <AppShell><div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div></AppShell>;
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl px-6 py-10 space-y-6">
        <div>
          <h1 className="font-display text-4xl font-bold">Configurações</h1>
          <p className="text-muted-foreground mt-2 text-sm">
            Personalize o cabeçalho dos PDFs gerados com o nome e logotipo da sua empresa ou escritório.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Marca no PDF</CardTitle>
            <CardDescription>
              Quando ativado, os PDFs exibirão seu logotipo e nome no topo. Quando desativado,
              será usado apenas o título padrão do PRONAF.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">Exibir marca personalizada</div>
                <div className="text-sm text-muted-foreground">
                  Desativar mantém o PDF com o padrão oficial PRONAF.
                </div>
              </div>
              <Switch
                checked={settings.branding_enabled}
                onCheckedChange={(v) => setSettings({ ...settings, branding_enabled: v })}
                data-testid="branding-toggle"
              />
            </div>
            {!settings.branding_enabled && (
              <div className="rounded-md border border-dashed p-3 text-xs text-muted-foreground bg-muted/40" data-testid="branding-disabled-hint">
                Os campos abaixo serão habilitados quando a marca personalizada for ativada.
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="company_name">Nome da empresa / escritório</Label>
              <Input
                id="company_name"
                value={settings.company_name || ""}
                onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                placeholder="Ex: AgroTec Assessoria Rural"
                disabled={!settings.branding_enabled}
                data-testid="company-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Logotipo (PNG ou JPG, até 500KB)</Label>
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <label
                    htmlFor="logo-upload"
                    className={`flex items-center justify-center gap-2 h-28 rounded-md border border-dashed ${settings.branding_enabled ? "cursor-pointer hover:bg-secondary" : "opacity-50 cursor-not-allowed"}`}
                  >
                    <UploadCloud className="h-5 w-5 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Enviar logotipo</span>
                    <input
                      id="logo-upload"
                      type="file"
                      accept="image/png,image/jpeg"
                      className="hidden"
                      onChange={onLogoChange}
                      disabled={!settings.branding_enabled}
                      data-testid="logo-upload-input"
                    />
                  </label>
                </div>
                {settings.logo_base64 && (
                  <div className="relative">
                    <img
                      src={settings.logo_base64}
                      alt="Logo"
                      className="h-28 w-28 object-contain bg-card border rounded-md p-2"
                    />
                    <button
                      type="button"
                      onClick={() => setSettings({ ...settings, logo_base64: "" })}
                      className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center"
                      data-testid="remove-logo-button"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="pt-2">
              <Button onClick={save} disabled={saving} data-testid="save-settings-button">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Salvar configurações
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
